
import React from 'react';
import { ArrowLeft, ArrowRight, List, MessageCircle } from 'lucide-react';
import { useAIAgent } from '../../context/AIAgentContext';

const RootCauseAnalysis = ({ level, onBack, onDrillDown, currentPage }) => {
  const { selectScenario } = useAIAgent();
  
  // Mock data - in a real implementation, this would be dynamic based on context
  const rootCauseData = {
    'competitor-360': {
      1: {
        title: "Market Share Analysis",
        causes: [
          "Competitor price strategy changes",
          "New competitor location openings",
          "Customer loyalty program effectiveness",
          "Regional market saturation"
        ]
      },
      2: {
        title: "Price Strategy Impact",
        causes: [
          "Elasticity response to competitor changes",
          "Fuel cost fluctuations affecting margins",
          "Promotion effectiveness variance",
          "Customer segment price sensitivity"
        ]
      },
      3: {
        title: "Customer Behavior Factors",
        causes: [
          "Demographic shifts in key areas",
          "Change in purchase frequency patterns",
          "Cross-purchase category correlations",
          "Traffic pattern modifications" 
        ]
      },
      4: {
        title: "Actionable Opportunities",
        causes: [
          "Strategic pricing adjustments",
          "Location-specific promotion targeting",
          "Competitive differentiator emphasis",
          "Customer experience enhancement areas"
        ]
      }
    },
    'trade-area-analyzer': {
      1: {
        title: "Traffic Flow Patterns",
        causes: [
          "Rush hour behavior changes",
          "Weekend vs weekday differences",
          "Seasonal traffic variation",
          "Construction impact zones"
        ]
      },
      2: {
        title: "Customer Origin Analysis",
        causes: [
          "Primary catchment area shifts",
          "Secondary trade area expansion",
          "Commuter route dependencies",
          "Residential vs commercial zones"
        ]
      },
      3: {
        title: "Purchase Behavior Drivers",
        causes: [
          "Convenience item attachment rate",
          "Fuel grade selection factors",
          "Time spent on premises correlation",
          "Payment method preferences"
        ]
      },
      4: {
        title: "Market Opportunity Identification",
        causes: [
          "Underserved customer segments",
          "Time-of-day service gaps",
          "Product category expansion potential",
          "Loyalty program enhancement targets"
        ]
      }
    },
    // Default fallback for other pages
    'default': {
      1: {
        title: "Primary Factors Analysis",
        causes: [
          "Market condition changes",
          "Customer behavior shifts",
          "Operational efficiency factors",
          "Competitive landscape developments"
        ]
      },
      2: {
        title: "Secondary Impact Assessment",
        causes: [
          "Revenue stream distribution",
          "Cost structure implications",
          "Customer segment performance",
          "Regional variation patterns"
        ]
      },
      3: {
        title: "Opportunity Identification",
        causes: [
          "Underperforming segments",
          "High-potential growth areas",
          "Efficiency improvement targets",
          "Strategic advantage points"
        ]
      },
      4: {
        title: "Action Planning",
        causes: [
          "Priority initiative identification",
          "Resource allocation optimization",
          "Implementation timeline factors",
          "Expected outcome projections"
        ]
      }
    }
  };
  
  // Get data for current page, fallback to default if not found
  const pageData = rootCauseData[currentPage] || rootCauseData['default'];
  const levelData = pageData[level] || pageData[1]; // Fallback to level 1 if requested level not found
  
  const handleScenarioSelect = (scenario) => {
    selectScenario({
      title: scenario,
      level: level
    });
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button 
          onClick={onBack}
          className="text-sm text-gray-600 flex items-center hover:text-gray-900"
        >
          <ArrowLeft size={14} className="mr-1" /> Back
        </button>
        <span className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded-full">
          Level {level} Analysis
        </span>
      </div>
      
      <h4 className="font-medium text-gray-800 mt-2">{levelData.title}</h4>
      
      <div className="space-y-3 mt-4">
        {levelData.causes.map((cause, index) => (
          <div key={index} className="border border-gray-200 rounded-md p-3 hover:bg-gray-50">
            <div className="flex justify-between items-start">
              <div className="flex items-start">
                <List size={16} className="text-purple-600 mt-1 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-800">{cause}</p>
                  <div className="flex mt-2 space-x-2">
                    <button 
                      onClick={() => handleScenarioSelect(cause)}
                      className="text-xs text-purple-700 flex items-center hover:underline"
                    >
                      <MessageCircle size={12} className="mr-1" /> Explore scenario
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {level < 4 && (
        <div className="mt-4 text-center">
          <button 
            onClick={onDrillDown}
            className="inline-flex items-center text-sm text-purple-700 hover:text-purple-900"
          >
            Analyze deeper <ArrowRight size={14} className="ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default RootCauseAnalysis;
